// shared-lib/cabinet-runs/peninsulaBack.js
//
// A peninsula arm's BACK: the 1" finished panel on its dining side and, behind
// a range that breaks its counter, the strip of counter over that panel.
//
// The generator used to emit both in its finishing pass, after the engine had
// built the arm (`materializeCandidate`), and no run owned either — a re-solve
// rebuilds only what a run owns, and the engine built no peninsula back. So a
// run edit could not reach the counter strip, and resizing the arm left the
// panel at the old length (census 2026-10-23, 115 generated layouts: 33
// panels, 14 strips, in every one of the 20 with a peninsula). The engine
// builds them now, on every solve of a run with an arm (`materializeRun`),
// generation included (`engineFill`), so the run owns what it builds and
// rebuilds it with the arm.
//
// The pieces keep the generator's geometry exactly; only the caller moved.
// Pure.

const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { withSlabCuts } = require('../kitchen-assembly/seatedSlabCut');
const {
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_THICKNESS,
} = require('../kitchen-assembly/constants');
const { objectFootprint } = require('../geometry/objectFootprint');
const { baysForRun, isApplianceKind } = require('./bays');
const { lookOf, wearLook } = require('./newRunFamily');

const INCHES_TO_METERS = 0.0254;
const EPS = 1e-6;
/** A body standing on the floor, not hung — the generator's cover test. */
const FLOOR_STANDING = 0.3;

/** The panel's thickness — the inch outboard of the arm's line. */
const PENINSULA_BACK_PANEL_THICKNESS = 1 * INCHES_TO_METERS;

/** Gaps below this are bridged rather than broken — a run's own joints. */
const PENINSULA_PANEL_BRIDGE = 2 * INCHES_TO_METERS;

/**
 * Objects that stand in a run's band without being cabinet BODY: the surfaces
 * and fittings above it, the stools in front of it, and the arm's own skin.
 */
const PENINSULA_NON_BODY_TYPES = new Set([
  'countertop',
  'upper-cabinet',
  'vent-hood',
  'bar-stool',
  'kitchen-sink',
  'kitchen-faucet',
  'fridge-surround',
]);

/** The id every back counter strip has carried, the generator's and ours. */
const BACK_COUNTER_ID_PREFIX = 'peninsula_back_counter_';

/** The id every back panel has carried, the generator's and ours. */
const BACK_PANEL_ID_PREFIX = 'peninsula_back_panel_';

/**
 * A peninsula arm's finished back panel. Marked `backPanel` since 2026-10-21;
 * the generator's, applied before, carry only its id. Read by the flag alone,
 * those were never superseded, and every re-solve of their arm stood the
 * run's own panel beside them — two back panels in one place.
 */
const isPeninsulaBackPanel = (o) =>
  o?.type === 'blank-panel' &&
  o.category?.peninsula === true &&
  (o.category?.backPanel === true ||
    String(o.id || '').startsWith(BACK_PANEL_ID_PREFIX));

/**
 * The counter strip over a peninsula's back panel behind a range. Marked
 * `backBridge` since the engine built it; the generator's, applied before,
 * carry only its id.
 */
const isPeninsulaBackBridge = (o) =>
  o?.type === 'countertop' &&
  o.category?.peninsula === true &&
  (o.category?.backBridge === true ||
    String(o.id || '').startsWith(BACK_COUNTER_ID_PREFIX));

/**
 * The along-wall spans a peninsula arm actually CARRIES, merged and clamped to
 * the wall — appliances, base cabinets, corner legs and filler panels, but not
 * the arm's own finish pieces.
 *
 * Asked span by span, because an arm can carry cabinetry at both ends and
 * nothing in between (a chase or a corridor refuses the middle). A full-length
 * back panel there stands a 1"-thick, 34.5"-tall strip of bare wood in the gap
 * with no cabinet in front of it.
 *
 * Spans are measured GEOMETRICALLY, in the wall's own frame, from the objects
 * that were actually EMITTED. Not from `parentWallIds`: an L corner cabinet is
 * parented to wall A alone, but its other leg genuinely stands on wall B and
 * the panel must run behind it. And not from a placement's reserved range,
 * which the built cabinet can end up narrower than (a sink reserved 0"-36"
 * whose base was trimmed to 2"-36" left a 2" stub of panel past the end of
 * the run). `extraSpans` are spans already in the wall's params — a solved
 * appliance bay, whose object the engine does not emit.
 *
 * @param {Object} wall the arm's (virtual) wall
 * @param {Object[]} objects what was emitted
 * @param {Array<{start:number, end:number}>} [extraSpans]
 * @returns {Array<{start:number, end:number}>}
 */
function peninsulaBodySpans(wall, objects, extraSpans = []) {
  const axes = getWallAxes(wall);
  if (!axes) return [];
  const depth = KITCHEN_CABINET_DIMENSIONS.depth;

  const spans = [...extraSpans];
  for (const o of objects) {
    const isBody =
      !PENINSULA_NON_BODY_TYPES.has(o.type) &&
      o.dimensions?.isUpper !== true &&
      // The arm's own skin finishes the run; it never justifies itself.
      !(o.type === 'blank-panel' && o.category?.peninsula);
    if (!isBody) continue;
    const corners = objectFootprint(o);
    if (!corners) continue;
    let u0 = Infinity;
    let u1 = -Infinity;
    let v0 = Infinity;
    for (const c of corners) {
      const dx = c.x - axes.start.x;
      const dz = c.z - axes.start.z;
      u0 = Math.min(u0, dx * axes.wallDir.x + dz * axes.wallDir.z);
      u1 = Math.max(u1, dx * axes.wallDir.x + dz * axes.wallDir.z);
      v0 = Math.min(v0, dx * axes.wallNormal.x + dz * axes.wallNormal.z);
    }
    // Standing ON this run: its back face ON the wall line (a millimetre of
    // float noise, no more) and its body inside the cabinet band. The
    // tolerance has to be tight — where an arm dies into a real wall, that
    // wall's own run crosses the arm's band with its back face less than an
    // inch outboard of the arm's line, and a loose window reads it as 24" of
    // peninsula that isn't there.
    if (v0 < -0.001 || v0 > depth) continue;
    spans.push({ start: u0, end: u1 });
  }

  const clamped = spans
    .map((s) => ({
      start: Math.max(0, Math.min(s.start, s.end)),
      end: Math.min(axes.wallLen, Math.max(s.start, s.end)),
    }))
    .filter((s) => s.end - s.start > 1e-6)
    .sort((a, b) => a.start - b.start);

  const merged = [];
  for (const s of clamped) {
    const last = merged[merged.length - 1];
    if (last && s.start <= last.end + PENINSULA_PANEL_BRIDGE) {
      last.end = Math.max(last.end, s.end);
    } else {
      merged.push({ ...s });
    }
  }
  return merged;
}

/**
 * The panel spans reaching the root the run TURNS at. The corner box there is
 * the other leg's reservation, and under the engine it holds nothing (a blank
 * box has a counter over it, no body — user ruling 2026-09-11), so the bodies
 * stop a depth short of the vertex; the corner says the run continues, and
 * the panel follows the run, not the boxes. Not at an EMPTY corner, where
 * nothing continues.
 */
function reachTurnedRoots(spans, { segIndex, seg, wallLen, lowerCorners }) {
  if (!spans.length) return spans;
  const reach = KITCHEN_CABINET_DIMENSIONS.depth + PENINSULA_PANEL_BRIDGE;
  const out = spans.map((s) => ({ ...s }));
  for (const c of lowerCorners || []) {
    if (!c || c.mode === 'empty') continue;
    const root =
      c.cornerIndex === segIndex
        ? seg.endParam
        : c.cornerIndex === segIndex - 1
          ? seg.startParam
          : null;
    if (!Number.isFinite(root)) continue;
    if (root <= EPS) {
      const first = out[0];
      if (first.start > 0 && first.start <= reach) first.start = 0;
    } else if (root >= wallLen - EPS) {
      const last = out[out.length - 1];
      if (last.end < wallLen && wallLen - last.end <= reach) last.end = wallLen;
    }
  }
  return out;
}

/**
 * The arm's back panels over its spans, on the OUTER side of its line, in the
 * look its cabinets wear. One builder for both callers: the engine, from what
 * it built, and the generator, which finishes the arm after pieces of its own
 * have joined it (a gap cover at the tip).
 */
function armBackPanels({ wall, spans, floorY, look }) {
  const axes = getWallAxes(wall);
  if (!axes) return [];
  return spans
    .map((span, index) => ({ span, index }))
    .filter(({ span }) => span.end - span.start > EPS)
    .map(({ span, index }) =>
      // Cut where a continuation piece meets its run at an angle, as its
      // seated slab would be (`seatedSlabCut.js`, `wall.cuts`).
      withSlabCuts(
        backPanel({ wall, axes, span, index, floorY, look }),
        wall,
        wall.cuts
      )
    );
}

/** The arm's back panel over one span, on the OUTER side of its line. */
function backPanel({ wall, axes, span, index, floorY, look }) {
  const spanLen = span.end - span.start;
  const offset = -PENINSULA_BACK_PANEL_THICKNESS / 2;
  const midAlong = (span.start + span.end) / 2;
  return wearLook(
    {
      id:
        index === 0
          ? `${BACK_PANEL_ID_PREFIX}${wall.id}`
          : `${BACK_PANEL_ID_PREFIX}${wall.id}_${Math.round(span.start * 1000)}`,
      type: 'blank-panel',
      label: 'Peninsula Back Panel',
      category: { 'blank-panel': true, peninsula: true, backPanel: true },
      center: {
        x:
          axes.start.x + midAlong * axes.wallDir.x + offset * axes.wallNormal.x,
        y: floorY + KITCHEN_CABINET_DIMENSIONS.height / 2,
        z:
          axes.start.z + midAlong * axes.wallDir.z + offset * axes.wallNormal.z,
      },
      dimensions: {
        x: spanLen,
        y: KITCHEN_CABINET_DIMENSIONS.height,
        z: PENINSULA_BACK_PANEL_THICKNESS,
        freestanding: true,
      },
      // Its finished face (local +Z) looks out at the dining side, against
      // the inward normal the arm's cabinets face along.
      rotation: { x: 0, y: axes.wallRotationY + Math.PI, z: 0 },
      parentWallIds: [wall.id],
      lastModified: new Date().toISOString(),
    },
    look
  );
}

/**
 * The counter strip over the back panel behind a range. The range eats the
 * front 24" of the arm at counter height, so the panel's inch would otherwise
 * have no counter above it. A SEATED arm's slab already covers it (its bar
 * behind the range, `seatedPeninsulaTop`), so none is built there.
 */
function backBridge({ wall, axes, bay, floorY }) {
  const len = bay.end - bay.start;
  const mid = (bay.start + bay.end) / 2;
  const offset = -PENINSULA_BACK_PANEL_THICKNESS / 2;
  const counterTopY =
    floorY + KITCHEN_CABINET_DIMENSIONS.height + KITCHEN_COUNTERTOP_THICKNESS;
  return {
    id: `${BACK_COUNTER_ID_PREFIX}${wall.id}_${Math.round(mid * 1000)}`,
    type: 'countertop',
    label: 'Peninsula Back Counter Bridge',
    category: { countertop: true, peninsula: true, backBridge: true },
    center: {
      x: axes.start.x + mid * axes.wallDir.x + offset * axes.wallNormal.x,
      y: counterTopY - KITCHEN_COUNTERTOP_THICKNESS / 2,
      z: axes.start.z + mid * axes.wallDir.z + offset * axes.wallNormal.z,
    },
    dimensions: {
      x: len,
      y: KITCHEN_COUNTERTOP_THICKNESS,
      z: PENINSULA_BACK_PANEL_THICKNESS,
    },
    rotation: { x: 0, y: axes.wallRotationY, z: 0 },
    parentWallIds: [wall.id],
    lastModified: new Date().toISOString(),
  };
}

const wallIdsOf = (o) => [
  ...(o.parentWallIds || []),
  ...(o.cabinetWallRefs || []).map((r) => r?.id),
];

/**
 * Every arm of a wall-backed run, finished: its back panels and the counter
 * strips behind its ranges. And the back pieces standing on those arms that
 * no run owns — a kitchen applied before the engine built them — which these
 * supersede: left in place they would stand doubled.
 *
 * @param {Object} args
 * @param {Object} args.run the stored run being solved
 * @param {Object[]} args.segments the run's wall-backed segments, resolved
 *   (`{ wall, startParam, endParam }`); an ARM is one on a virtual wall
 * @param {Object} args.out `fillRun`'s result: `cabinets`, `fillers`,
 *   `cornerUnits`, the solved `bays`, the `lowerCorners` report
 * @param {number} args.floorY
 * @param {Object[]} [args.roomObjects] the room as it stands
 * @returns {{ objects: Object[], supersededIds: string[] }}
 */
function peninsulaArmBacks({ run, segments, out, floorY, roomObjects = [] }) {
  const objects = [];
  const armIds = new Set();
  // Everything standing on an arm: what this solve built, the solved spans of
  // its appliances (below, per arm), and whatever else stands there that the
  // run does not own — the generator's gap cover at an arm's tip — exactly
  // the bodies the generator's own panel ran behind. Not the run's old pieces
  // (this solve replaces them) and not an appliance it holds a bay for (its
  // bay is where it is going).
  const bayAppliances = new Set(
    baysForRun(run)
      .map((b) => b?.applianceId)
      .filter(Boolean)
  );
  const bodies = [
    ...(out.cabinets || []),
    ...(out.fillers || []),
    ...(out.cornerUnits || []),
    ...(roomObjects || []).filter(
      (o) =>
        o?.center &&
        o.runId !== run?.id &&
        !bayAppliances.has(o.id) &&
        o.center.y - (o.dimensions?.y || 0) / 2 <= floorY + FLOOR_STANDING
    ),
  ];
  (segments || []).forEach((seg, segIndex) => {
    const wall = seg?.wall;
    // A DRAWN island leg finishes its own back (`bentRunFill.js` hands it to
    // the free-standing fill, which spends the panel out of the leg's depth).
    if (!wall?.isVirtual || wall.drawnFreeLeg) return;
    const axes = getWallAxes(wall);
    if (!axes) return;
    const bays = (out.bays || []).filter(
      (b) => b.segIndex === segIndex && isApplianceKind(b.kind)
    );
    const spans = reachTurnedRoots(
      peninsulaBodySpans(
        wall,
        bodies,
        bays.map((b) => ({ start: b.start, end: b.end }))
      ),
      { segIndex, seg, wallLen: axes.wallLen, lowerCorners: out.lowerCorners }
    );
    // A peninsula IS its base cabinetry: an arm the walkway emptied gets no
    // skin floating mid-room.
    if (!spans.length) return;
    armIds.add(wall.id);
    // The panel is the arm's finished back, so it wears what the arm's
    // cabinets wear.
    const armCabinet = (out.cabinets || []).find(
      (o) =>
        o.type === 'lower-cabinet' &&
        !o.dimensions?.isUpper &&
        wallIdsOf(o).includes(wall.id)
    );
    const look = armCabinet ? lookOf(armCabinet) : null;
    objects.push(...armBackPanels({ wall, spans, floorY, look }));
    const seating = wall.seating;
    for (const bay of bays) {
      if (bay.kind !== 'range') continue;
      if (
        seating &&
        Math.min(bay.end, seating.end) - Math.max(bay.start, seating.start) >
          EPS
      )
        continue;
      objects.push(backBridge({ wall, axes, bay, floorY }));
    }
  });
  const supersededIds = objects.length
    ? (roomObjects || [])
        .filter(
          (o) =>
            o?.id &&
            !o.runId &&
            (isPeninsulaBackPanel(o) || isPeninsulaBackBridge(o)) &&
            wallIdsOf(o).some((id) => armIds.has(id))
        )
        .map((o) => o.id)
    : [];
  return { objects, supersededIds };
}

module.exports = {
  armBackPanels,
  PENINSULA_BACK_PANEL_THICKNESS,
  PENINSULA_PANEL_BRIDGE,
  isPeninsulaBackPanel,
  isPeninsulaBackBridge,
  peninsulaBodySpans,
  peninsulaArmBacks,
};
